import React, { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { SkeletonRow } from "@/components/ui/Skeleton";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { UserType } from "@/types/db/users";
import { EmergencyContactType } from "@/types/db/user_emergency_contacts";
import { useTheme } from "@/hooks/useTheme";
import usePermissions from "@/hooks/usePermissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { EmergencyContactCard } from "./EmergencyContactCard";
import { userService } from "@/services/userService";
import useToast from "@/hooks/useToast";

export interface EmergencyContactsTabProps {
  externalAddTrigger?: number;
}

type PhoneData = {
  type: "cell" | "home" | "work";
  number: string;
};

type EmailData = {
  type: "personal" | "work";
  address: string;
};

export function EmergencyContactsTab({ externalAddTrigger }: EmergencyContactsTabProps) {
  const { colors } = useTheme();
  const { entityData, loading } = useEntityEdit<UserType>();
  const { user_emergency_contacts: ecPermissions } = usePermissions();
  const toast = useToast();

  // Local state for contacts - independent of parent edit state
  const [contacts, setContacts] = useState<EmergencyContactType[]>([]);
  const [saving, setSaving] = useState(false);

  // Sync local state with entity data
  useEffect(() => {
    if (entityData?.user_emergency_contacts) {
      setContacts(entityData.user_emergency_contacts);
    }
  }, [entityData?.user_emergency_contacts]);

  // Handle external add trigger from header button
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0 && ecPermissions.create) {
      handleAddContact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAddTrigger, ecPermissions.create]);

  // Save contacts to API
  const saveContacts = useCallback(
    async (updatedContacts: EmergencyContactType[]) => {
      if (!entityData?.id) return;

      setSaving(true);
      try {
        await userService.syncUserEmergencyContacts(entityData.id, updatedContacts);
        setContacts(updatedContacts);
        toast.showToast({ title: "Saved", message: "Emergency contacts updated", toastType: "success" });
      } catch (error: any) {
        toast.showToast({ title: "Error", message: error.message, toastType: "error" });
        // Revert to previous state
        setContacts(entityData.user_emergency_contacts || []);
      } finally {
        setSaving(false);
      }
    },
    [entityData?.id, entityData?.user_emergency_contacts, toast],
  );

  const handleAddContact = useCallback(() => {
    const newContact: Partial<EmergencyContactType> = {
      first_name: "",
      last_name: "",
      relationship: "",
      phones: [] as PhoneData[],
      emails: [] as EmailData[],
    };

    const updatedContacts = [...contacts, newContact as EmergencyContactType];
    setContacts(updatedContacts);
    // Don't save yet - will save when user fills in and blurs
  }, [contacts]);

  const handleRemoveContact = useCallback(
    async (index: number) => {
      const updated = contacts.filter((_, i) => i !== index);
      await saveContacts(updated);
    },
    [contacts, saveContacts],
  );

  const handleUpdateContact = useCallback((index: number, updates: Partial<EmergencyContactType>) => {
    setContacts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      return updated;
    });
  }, []);

  const handleSaveContact = useCallback(
    async (index: number) => {
      const contact = contacts[index];
      // Only save if contact has required fields
      if (!contact.first_name?.trim() || !contact.last_name?.trim()) {
        toast.showToast({
          title: "Validation Error",
          message: "First and last name are required",
          toastType: "error",
        });
        return;
      }
      await saveContacts(contacts);
    },
    [contacts, saveContacts, toast],
  );

  // If no read permission, show access denied
  if (!ecPermissions.read) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>
          You don&apos;t have permission to view emergency contacts
        </ThemedText>
      </ScrollView>
    );
  }

  if (loading || !entityData) {
    return (
      <View style={styles.container}>
        <View style={styles.section}>
          <SkeletonRow />
          <SkeletonRow />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} pointerEvents={saving ? "none" : "auto"}>
      <ScrollView style={styles.scrollContent}>
        {contacts.length > 0 ? (
          <View style={styles.contactList}>
            {contacts.map((contact, index) => (
              <EmergencyContactCard
                key={contact.id || `new-${index}`}
                contact={contact}
                index={index}
                canUpdate={ecPermissions.update}
                canDelete={ecPermissions.delete}
                onUpdate={handleUpdateContact}
                onSave={handleSaveContact}
                onRemove={handleRemoveContact}
              />
            ))}
          </View>
        ) : (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="account-alert-outline" size={48} color={colors.textSecondary} />
            <ThemedText style={{ opacity: 0.7, marginTop: 12 }}>No emergency contacts</ThemedText>
            {ecPermissions.create && (
              <ThemedText style={{ opacity: 0.5, marginTop: 4, fontSize: 12 }}>Use the + button to add one</ThemedText>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flex: 1,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
  },
  contactList: {
    gap: 12,
    padding: 16,
  },
  permissionNotice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    margin: 16,
    borderRadius: 8,
  },
});
